import { useRef, useEffect, useState } from "react";
import confetti from "canvas-confetti";
import correctSound from "@/assets/sounds/correct_answer.mp3";

const confettiConfig = {
	particleCount: 300,
	spread: 100,
	startVelocity: 60,
	gravity: 1.8,
	drift: 0.1,
	ticks: 400,
	decay: 0.94,
	scalar: 2,
};

export const useConfettiWithSound = () => {
	const audioRef = useRef(null);
	const [soundDuration, setSoundDuration] = useState(0);

	// Pre-load the audio when the hook is initialized
	useEffect(() => {
		audioRef.current = new Audio(correctSound);

		// Set up event listener to get duration once loaded
		const handleLoadedMetadata = () => {
			setSoundDuration(audioRef.current.duration * 1000); // Convert to milliseconds
		};

		audioRef.current.addEventListener(
			"loadedmetadata",
			handleLoadedMetadata
		);

		// If already loaded, get duration
		if (audioRef.current.readyState >= 2) {
			setSoundDuration(audioRef.current.duration * 1000);
		}

		return () => {
			if (audioRef.current) {
				audioRef.current.removeEventListener(
					"loadedmetadata",
					handleLoadedMetadata
				);
				audioRef.current.pause();
				audioRef.current.currentTime = 0;
			}
		};
	}, []);

	const triggerConfetti = async () => {
		try {
			confetti({ ...confettiConfig, origin: { x: 0, y: 1 }, angle: 45 });
			confetti({ ...confettiConfig, origin: { x: 1, y: 1 }, angle: 135 });

			if (audioRef.current) {
				audioRef.current.pause();
				audioRef.current.currentTime = 0;

				try {
					await audioRef.current.play();
				} catch (audioError) {
					console.error("Audio play failed:", audioError);

					const fallbackAudio = new Audio(correctSound);
					await fallbackAudio.play();
				}
			}

			return true;
		} catch (error) {
			console.error("Error in confetti or sound:", error);
			return false;
		}
	};

	// Clean up function to stop sound
	const cleanup = () => {
		if (audioRef.current) {
			audioRef.current.pause();
			audioRef.current.currentTime = 0;
		}
	};

	return { triggerConfetti, cleanup, soundDuration };
};
